/**
 * DubbingTab Component
 *
 * Main component for the Audio section's dubbing tab.
 * Contains two sub-tabs:
 *   - "Dub"            → the original dubbing panel (DubPanel)
 *   - "Subtitles Only" → standalone subtitle generation (SubtitlesOnlyPanel),
 *                        desktop only -- see isSubtitlesOnlyAvailable()
 *
 * Where "Subtitles Only" is unavailable there is nothing to switch between, so
 * the sub-tab bar is not rendered at all and the tab is just DubPanel.
 *
 * Sub-tab state is local to this component so each panel retains its own
 * context independently.
 */

import React, { useState, useCallback, memo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Mic2, Captions } from 'lucide-react';
import { SubTabBar, type SubTabItem } from '@/components/common';
import { LoginModal } from '../auth';
import { DubbingProgressTracker } from './DubbingProgressTracker';
import { DubbingProvider } from './DubbingContext';
import { useDubbing } from './DubbingContext.context';
import {
  DubbingHeader,
  MediaUploadSection,
  LanguageConfigSection,
  AdvancedSettingsSection,
  DubbingResultsSection,
} from './sections';
import { SubtitlesOnlyPanel } from '@/components/subtitles/SubtitlesOnlyPanel';
import { isSubtitlesOnlyAvailable } from '@/components/subtitles/availability';
import { TokenEstimateCard } from '@/components/tokens/TokenEstimateCard';
import { useTranslation } from '@/hooks/useTranslation';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { costGateBlockLabel, type CostGate } from '../../utils/costGate';

// ============================================================================
// Sub-tab definition
// ============================================================================

type DubbingSubTab = 'dub' | 'subtitles';

const SUB_TABS: Array<{
  id: DubbingSubTab;
  labelKey: string;
  defaultLabel: string;
  icon: LucideIcon;
}> = [
  { id: 'dub', labelKey: 'dubbing.subTabs.dub', defaultLabel: 'Dub', icon: Mic2 },
  {
    id: 'subtitles',
    labelKey: 'dubbing.subTabs.subtitlesOnly',
    defaultLabel: 'Subtitles Only',
    icon: Captions,
  },
];

// ============================================================================
// Original dubbing panel (extracted from the previous DubbingTabInner)
// ============================================================================

function DubPanel(): JSX.Element {
  const { t } = useTranslation();
  const { isLoginModalOpen, loginMessage, closeLoginModal } = useRequireAuth();

  const {
    state,
    actions,
    isUploading,
    isDubbing,
    hasResult,
    showForm,
    costGate,
    setCostGate,
    estimateOperationPath,
    estimateParams,
    handleStartDubbing,
    handleCancel,
    handleDownload,
    handleOpenFolder,
  } = useDubbing();

  const handleEstimateChange = useCallback(
    ({ status }: CostGate) => {
      setCostGate(status);
    },
    [setCostGate]
  );

  return (
    <div className="max-w-5xl mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 py-4 xs:py-6 sm:py-8">
      {/* Header */}
      <DubbingHeader />

      {/* Main content - Form */}
      {showForm && (
        <div className="space-y-4 xs:space-y-6">
          {/* Media uploader */}
          <MediaUploadSection />

          {/* Language selection */}
          {state.mediaFile && <LanguageConfigSection />}

          {/* Advanced settings (video only - LLM refinement is for transcription) */}
          {state.mediaFile && state.voiceConfigs.length > 0 && state.mediaType !== 'audio' && (
            <AdvancedSettingsSection />
          )}

          {/* Token estimate */}
          {state.mediaFile && state.voiceConfigs.length > 0 && estimateParams && (
            <div className="max-w-md mx-auto">
              <TokenEstimateCard
                operationPath={estimateOperationPath}
                parameters={estimateParams}
                onEstimateChange={handleEstimateChange}
              />
            </div>
          )}

          {/* Start button */}
          {state.mediaFile && state.voiceConfigs.length > 0 && (
            <div className="flex justify-center">
              <button
                onClick={handleStartDubbing}
                disabled={isUploading || isDubbing || costGate !== 'affordable'}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {isUploading
                  ? t('dubbing.buttons.uploading', { defaultValue: 'Uploading...' })
                  : (costGateBlockLabel(costGate, t) ??
                    t('dubbing.buttons.startDubbing', { defaultValue: 'Start Dubbing' }))}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Progress tracker */}
      {isDubbing && state.dubbingProgress && (
        <div className="space-y-6">
          <DubbingProgressTracker progress={state.dubbingProgress} mediaType={state.mediaType} />

          {/* Cancel button */}
          <div className="flex justify-center">
            <button
              onClick={handleCancel}
              className="px-6 py-2 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 transition-colors"
            >
              {t('dubbing.buttons.cancel', { defaultValue: 'Cancel' })}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {hasResult && (
        <DubbingResultsSection
          dubbingResult={state.dubbingResult}
          audioDubbingResult={state.audioDubbingResult}
          mediaType={state.mediaType}
          targetLanguagesCount={state.targetLanguages.length}
          downloadUrl={state.downloadUrl}
          localOutputPath={state.localOutputPath}
          onDownload={handleDownload}
          onOpenFolder={handleOpenFolder}
          onReset={actions.reset}
        />
      )}

      {/* YouTube publish entry-points removed during API v3 migration
          (2026-05-26). Will be re-added by the OAuth-based wizard. */}

      {/* Login Modal */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} message={loginMessage} />
    </div>
  );
}

// ============================================================================
// Top-level tab component with sub-tab switcher
// ============================================================================

function DubbingTabInner(): JSX.Element {
  const { t } = useTranslation();
  const [activeSubTab, setActiveSubTab] = useState<DubbingSubTab>('dub');

  if (!isSubtitlesOnlyAvailable()) {
    return <DubPanel />;
  }

  const items: SubTabItem[] = SUB_TABS.map(({ id, labelKey, defaultLabel, icon }) => ({
    id,
    label: t(labelKey, { defaultValue: defaultLabel }),
    icon,
    testId: `dubbing-subtab-${id}`,
  }));

  return (
    <div>
      <SubTabBar
        tabs={items}
        activeId={activeSubTab}
        onChange={(id) => setActiveSubTab(id as DubbingSubTab)}
        accent="purple"
      />

      {activeSubTab === 'dub' ? <DubPanel /> : <SubtitlesOnlyPanel />}
    </div>
  );
}

export const DubbingTab: React.FC = memo(function DubbingTab() {
  return (
    <DubbingProvider>
      <DubbingTabInner />
    </DubbingProvider>
  );
});
